import React from 'react'
import Group from 'components/utils/Group'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import DestinationListItem from './DestinationListItem'
import type { WrappedData, WrappedTotalsData } from '../../../types/wrapped'
import confettiAnimation from '../../../Animations/flex_confetti.json'
import WrappedLottieAnimation from '../../WrappedLottieAnimation'
import { rem } from 'polished'
import styled from 'styled-components'
import zIndex from 'styles/tools/z-index'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'

const ConfettiOverlay = styled(WrappedLottieAnimation)`
  position: absolute;
  top: 33%;
  left: ${rem(-20)};
  right: ${rem(-20)};
  transform: translateY(-33%);
  z-index: ${zIndex.menuInModal};
  pointer-events: none;
`

interface Props {
  topDestinations: WrappedData['topDestinations']
  stats: WrappedData['stats']
  phase: 'question' | 'answer'
  correctAnswerId: string
  selectedId: string | null
  onSelect: (destinationId: string) => void
  className?: string
  isTotalsMode?: boolean
  totalsData?: WrappedTotalsData
}

function QuizContent({
  topDestinations,
  stats,
  phase,
  correctAnswerId,
  selectedId,
  className,
  onSelect,
  isTotalsMode = false,
  totalsData,
}: Props) {
  const isCorrect = selectedId === correctAnswerId

  const totalDestinationsSearched = totalsData?.stats.destinationsSearched ?? 0

  const title = isTotalsMode
    ? "Can you guess our travellers' top viewed destination of 2025?"
    : 'Can you guess your top viewed destination of 2025?'

  return (
    <div className={className}>
      <Group direction="vertical" gap={12}>
        <Group direction="vertical" gap={4}>
          <Caption colour="highlight-primary" variant="small" format="uppercase">
            Top Viewed Destinations
          </Caption>
          <Heading variant="heading5">
            <b>{title}</b>
          </Heading>
          <BodyText variant="medium">
            {isTotalsMode
              ? <>Our travellers searched <b><FormatDecimal value={totalDestinationsSearched} /> destinations</b> last year.</>
              : <>You searched <b>{stats.destinationsSearched || 3} destinations</b> last year.</>}
          </BodyText>
        </Group>
        <Group direction="vertical" gap={16}>
          {topDestinations.map((destination) => (
            <DestinationListItem
              key={destination.id}
              destination={destination}
              phase={phase}
              isCorrect={destination.id === correctAnswerId}
              isSelected={destination.id === selectedId}
              onSelect={onSelect}
            />
          ))}
        </Group>
      </Group>
      {isCorrect && <ConfettiOverlay animationData={confettiAnimation} position="bottom" />}
    </div>
  )
}

export default React.memo(QuizContent)
